// Dark Web Monitoring mock data.
// Each user has 0-N breach incidents across the last 18 months. Each incident
// is either "email-only" (the email address appeared in a paste/breach) or
// "email + password" (credentials were exposed). Counts are deterministic
// per user so the page is stable across reloads, seeded off the email.
//
// Calculations:
//   - djb2 hash of email -> incident count = seed % 9 (so 0-8 per user).
//   - day offset = (seed + i*113) % 540 to spread across ~18 months.
//   - credential vs email-only split: ((seed + i) % 3) !== 0, so ~66% creds.
//   - DWM_SUMMARY: totals over RECORDS; emailsScanned excludes disabled users.
//   - DWM_TREND: 18 monthly buckets keyed by YYYY-MM, stacked creds + email.
//
// Postgres mapping:
//   - canonical table `breach_mentions` (id, user_id, source, type,
//     observed_at, year_month, breach_id) populated by the integration with
//     the breach-feed provider.
//   - lookup table `breach_sources` (id, label, severity_tier) so the
//     11 hard-coded names here become an extensible enum + display label.
//   - materialised view `dwm_per_email_v` (user_id, mentions, with_creds,
//     sources, latest_at) refreshed every few hours.
//   - materialised view `dwm_trend_v` bucketed monthly for 18-month window.
//
// New data points:
//   - per-source severity and provider attribution (not modelled here).
//   - resolution / acknowledgement state per mention (acknowledged_at,
//     acknowledged_by) so admins can clear false positives.
//   - password-strength signal alongside email_password (cleartext vs hash).
//
// Integration hook:
//   - `dwmApi` slice in src/store/services/ with endpoints `useDwmSummaryQuery`,
//     `useDwmByEmailQuery`, `useDwmTrendQuery`. Cache TTL 6 hours since
//     breach feeds update infrequently. Gated by FEATURES.darkWebMonitoring.

import { USERS } from './data';

const BREACH_SOURCES = [
    'LinkedIn 2024 leak', 'Snowflake credential dump', 'MOVEit breach', 'Twitter 2023 paste',
    'Adobe customer leak', 'Dropbox credential dump', 'LastPass vault leak', 'MyFitnessPal breach',
    'Anthem health records', 'Marriott reservations', 'Equifax credentials',
];

function hash(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0;
    return h;
}

const RECORDS = [];
for (const u of USERS) {
    if (u.disabled) continue;
    const seed = hash(u.email);
    const incidents = seed % 9; // 0-8 breaches
    for (let i = 0; i < incidents; i++) {
        const offset = (seed + i * 113) % 540; // distribute across ~18 months
        const date = new Date('2026-05-07T00:00:00Z');
        date.setUTCDate(date.getUTCDate() - offset);
        const isCreds = ((seed + i) % 3) !== 0; // ~66% of breaches expose creds
        RECORDS.push({
            email: u.email,
            name: u.name,
            dept: u.dept,
            location: u.location,
            source: BREACH_SOURCES[(seed + i * 7) % BREACH_SOURCES.length],
            type: isCreds ? 'email_password' : 'email_only',
            date: date.toISOString().slice(0, 10),
            yearMonth: date.toISOString().slice(0, 7),
        });
    }
}

export const DWM_RECORDS = RECORDS.sort((a, b) => (a.date < b.date ? 1 : -1));

// Aggregates the page needs at a glance.
export const DWM_SUMMARY = (() => {
    const emails = new Set(RECORDS.map((r) => r.email));
    const credentialBreaches = RECORDS.filter((r) => r.type === 'email_password').length;
    const emailOnlyBreaches  = RECORDS.filter((r) => r.type === 'email_only').length;
    return {
        totalBreaches: RECORDS.length,
        emailsBreached: emails.size,
        credentialBreaches,
        emailOnlyBreaches,
        emailsScanned: USERS.filter((u) => !u.disabled).length,
    };
})();

// Per-email aggregate, sorted by total mentions desc.
export const DWM_BY_EMAIL = (() => {
    const map = new Map();
    for (const r of RECORDS) {
        if (!map.has(r.email)) {
            map.set(r.email, { email: r.email, name: r.name, dept: r.dept, location: r.location, mentions: 0, withCreds: 0, sources: new Set(), latest: r.date });
        }
        const e = map.get(r.email);
        e.mentions++;
        if (r.type === 'email_password') e.withCreds++;
        e.sources.add(r.source);
        if (r.date > e.latest) e.latest = r.date;
    }
    return [...map.values()]
        .map((e) => ({ ...e, sources: e.sources.size }))
        .sort((a, b) => b.mentions - a.mentions);
})();

// Monthly trend over the last 18 months.
export const DWM_TREND = (() => {
    const months = [];
    const anchor = new Date('2026-05-07T00:00:00Z');
    for (let i = 17; i >= 0; i--) {
        const d = new Date(anchor.getTime());
        d.setUTCMonth(d.getUTCMonth() - i);
        months.push({ key: d.toISOString().slice(0, 7), label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }), creds: 0, emailOnly: 0 });
    }
    const idx = new Map(months.map((m, i) => [m.key, i]));
    for (const r of RECORDS) {
        const i = idx.get(r.yearMonth);
        if (i == null) continue;
        if (r.type === 'email_password') months[i].creds++;
        else                              months[i].emailOnly++;
    }
    return months;
})();
